import { useEffect } from "react";

import { useSearchParams } from "react-router";

import { GetSessionQuery, useGetSessionQuery } from "@/graphql";

type Session = NonNullable<
  NonNullable<GetSessionQuery["coachSession"]>["session"]
>;

type Product = NonNullable<
  NonNullable<GetSessionQuery["coachSession"]>["session"]
>["product"];

type CoachProduct = NonNullable<
  NonNullable<GetSessionQuery["coachSession"]>["coach"]
>["coachProduct"];

interface UseSessionProps {
  coachId: string;
  errorMessage: string | null;
  selectedSession: Session | null;
  onSelectSession: (session: Session) => void;
  onSelectProduct: (product: Product, coachProduct: CoachProduct) => void;
  onSessionError: (errorMessage: string) => void;
}

export function useSession({
  coachId,
  errorMessage,
  selectedSession,
  onSelectSession,
  onSelectProduct,
  onSessionError,
}: UseSessionProps) {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("programComponentId");
  const { data, loading } = useGetSessionQuery({
    variables: { coachId, sessionId: sessionId ?? "" },
    skip: !sessionId,
  });

  // Handle error case
  useEffect(() => {
    if (data?.coachSession?.error && !errorMessage) {
      onSessionError(
        data.coachSession.error.message ?? "Error fetching session",
      );
      return;
    }
  }, [data?.coachSession?.error, errorMessage]);

  // Handle success case
  useEffect(() => {
    if (data?.coachSession?.session && !selectedSession) {
      onSelectSession(data.coachSession.session);
      if (data.coachSession.session.product && data.coachSession.coach) {
        onSelectProduct(
          data.coachSession.session.product,
          data.coachSession.coach.coachProduct,
        );
      }
    }
  }, [data?.coachSession?.session, selectedSession]);

  return { loading };
}
